import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@racoon/ui";
import { ChevronRight } from "lucide-react";
import { JobStatus, RouteStatus } from "@racoon/api";
import { useT, Namespace } from "@racoon/i18n";

interface EtaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: any;
  getEtaBreakdown: () => {
    jobs: Array<{
      id: number;
      depot_to_job: string;
      service_time: string;
      job_to_depot: string;
      is_first: boolean;
      is_last: boolean;
      original_job: any;
    }>;
    total_time: string;
  };
}

export function EtaModal({ open, onOpenChange, data, getEtaBreakdown }: EtaModalProps) {
  const t = useT();
  const breakdown = getEtaBreakdown();
  const [expandedJobIds, setExpandedJobIds] = useState<Set<number>>(
    new Set(breakdown.jobs.map((job) => job.id)),
  );

  // Reset expanded jobs when modal opens
  useEffect(() => {
    if (open) {
      const breakdown = getEtaBreakdown();
      setExpandedJobIds(new Set(breakdown.jobs.map((job) => job.id)));
    }
  }, [open]);

  const calculateETA = (date: string | null | undefined, status: RouteStatus) => {
    if (!date) {
      return t(Namespace.Infopanel, "empty");
    }

    const d = new Date(date);
    const now = new Date();
    const diff = d.getTime() - now.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes <= 0 || status === RouteStatus.ROUTE_STATUS_COMPLETED) {
      return t(Namespace.Infopanel, "eta.arrived");
    }

    return t(Namespace.Infopanel, "eta.minutesLeft", { minutes });
  };

  // Function to determine status circle styles
  const getStatusCircleStyles = (
    job: {
      status: JobStatus;
      original_job: {
        fact_start_at?: Date | null;
        fact_end_at?: Date | null;
      };
    },
    stage: "depot_to_job" | "service" | "job_to_depot",
  ) => {
    const { status, original_job } = job;

    // Logic for different stages
    switch (stage) {
      case "depot_to_job":
        // Depot → Job: complete if job has started
        if (original_job.fact_start_at) {
          return "bg-gray-500"; // Gray - already done
        }
        return "bg-white border border-blue-500"; // White - not started yet

      case "service":
        // Service: complete if job is done
        if (status === JobStatus.JOB_STATUS_COMPLETED) {
          return "bg-gray-500"; // Gray - already done
        }
        // In progress if job started but not finished
        if (original_job.fact_start_at && !original_job.fact_end_at) {
          return "bg-blue-500"; // Blue - in progress
        }
        return "bg-white border border-blue-500"; // White - not started yet

      case "job_to_depot":
        // Job → Depot: complete if job is done
        if (status === JobStatus.JOB_STATUS_COMPLETED) {
          return "bg-gray-500"; // Gray - already done
        }
        // In progress if job done but route not finished
        if (original_job.fact_end_at) {
          return "bg-blue-500"; // Blue - in progress
        }
        return "bg-white border border-blue-500"; // White - not started yet

      default:
        return "bg-white border border-blue-500";
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) setExpandedJobIds(new Set(breakdown.jobs.map((job) => job.id)));
      }}
    >
      <DialogContent className="!max-w-[25rem]">
        <DialogHeader>
          <DialogTitle className="!font-bold text-2xl">
            {t(Namespace.Infopanel, "eta.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          <div className="text-sm space-y-2">
            {data?.plan_start_at && data?.plan_end_at && data?.jobs?.length ? (
              <>
                {/* Details for each job */}
                {(() => {
                  const breakdown = getEtaBreakdown();
                  return breakdown.jobs.map((job) => (
                    <div key={job.id} className="bg-text-invert rounded-lg">
                      <div
                        className={`flex bg-white justify-between items-center cursor-pointer 
                         hover:bg-gray-50 !max-h-12 p-4 border border-gray-200 rounded-lg
                          ${expandedJobIds.has(job.id) ? "border-b-1 rounded-b-none" : ""}`}
                        onClick={() => {
                          setExpandedJobIds((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has(job.id)) {
                              newSet.delete(job.id);
                            } else {
                              newSet.add(job.id);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <div className="flex items-center gap-1">
                          <ChevronRight
                            className={`text-text-primary transition-transform ${expandedJobIds.has(job.id) ? "rotate-90 !text-brand-blue" : ""}`}
                          />
                          <div className="font-semibold text-base text-text-primary">
                            {" "}
                            Z{job.id}
                          </div>
                        </div>

                        <span className="font-medium text-text-primary text-sm">
                          {job.original_job.pickup_location?.name}
                        </span>
                      </div>

                      {expandedJobIds.has(job.id) && (
                        <>
                          <div className="bg-slate-50 rounded-lg border rounded-t-none border-t-0">
                            <div className="p-4 pb-8 ">
                              <div className="flex items-start">
                                {/* Vertical line */}
                                <div className="flex flex-col mt-0.5 items-center mr-3">
                                  <div
                                    className={`w-4 h-4 rounded-full ${getStatusCircleStyles({ status: job.original_job.status, original_job: job.original_job }, "depot_to_job")}`}
                                  ></div>
                                  <div className="w-0.5 h-3.5 bg-gray-300"></div>
                                  <div
                                    className={`w-4 h-4 rounded-full ${getStatusCircleStyles({ status: job.original_job.status, original_job: job.original_job }, "service")}`}
                                  ></div>
                                  <div className="w-0.5 h-3.5 bg-gray-300"></div>
                                  <div
                                    className={`w-4 h-4 rounded-full ${getStatusCircleStyles({ status: job.original_job.status, original_job: job.original_job }, "job_to_depot")}`}
                                  ></div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 space-y-2.5">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-text-primary">
                                      {job.is_first ? "Депо - Заказ" : "Предыдущее задание - Заказ"}
                                    </span>
                                    <span className="font-medium text-sm">{job.depot_to_job}</span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-text-primary">Обслуживание</span>
                                    <span className="font-medium text-sm">{job.service_time}</span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-text-primary">
                                      {job.is_last ? "Заказ - Депо" : "Заказ - Следующее задание"}
                                    </span>
                                    <span className="font-medium text-sm">{job.job_to_depot}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Debug info */}
                            <div className="space-y-3">
                              <div className="flex px-4 justify-between">
                                <div className="text-sm">Начато </div>
                                <div className="font-medium text-sm text-text-primary">
                                  {job.original_job.fact_start_at
                                    ? `${new Date(job.original_job.fact_start_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })} ${new Date(job.original_job.fact_start_at).toLocaleDateString("ru-RU")}`
                                    : job.original_job.plan_start_at
                                      ? `${new Date(job.original_job.plan_start_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })} ${new Date(job.original_job.plan_start_at).toLocaleDateString("ru-RU")}`
                                      : "N/A"}
                                </div>
                              </div>
                              <div className="flex px-4 justify-between">
                                <div className="text-sm text-text-primary">Окончание </div>
                                <div className="font-medium text-sm text-text-primary">
                                  {job.original_job.fact_end_at
                                    ? `${new Date(job.original_job.fact_end_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })} ${new Date(job.original_job.fact_end_at).toLocaleDateString("ru-RU")}`
                                    : job.original_job.plan_end_at
                                      ? `${new Date(job.original_job.plan_end_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })} ${new Date(job.original_job.plan_end_at).toLocaleDateString("ru-RU")}`
                                      : "N/A"}
                                </div>
                              </div>
                              <div className="flex px-4  justify-between">
                                <div className="text-sm text-text-primary">
                                  Общее планируемое время{" "}
                                </div>
                                <div className="font-medium text-sm text-text-primary">
                                  {(() => {
                                    const depotToJob = parseInt(
                                      job.depot_to_job.replace(" мин", ""),
                                    );
                                    const serviceTime = parseInt(
                                      job.service_time.replace(" мин", ""),
                                    );
                                    const jobToDepot = parseInt(
                                      job.job_to_depot.replace(" мин", ""),
                                    );
                                    const total = depotToJob + serviceTime + jobToDepot;
                                    return `${total} мин`;
                                  })()}
                                </div>
                              </div>
                              <div className="flex px-4 pb-4 justify-between">
                                <div className="text-sm text-text-primary">Ожидаемое прибытие</div>
                                <div className="font-medium text-sm text-text-primary">
                                  {calculateETA(
                                    data.plan_end_at,
                                    RouteStatus.ROUTE_STATUS_COMPLETED,
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ));
                })()}
              </>
            ) : (
              <div className="text-center text-gray-500">
                {t(Namespace.Infopanel, "eta.noData")}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
